<template>
  <v-card elevation="0" class="pa-2 mt-2">
    <v-card-actions class="pl-3" style="max-width: 10rem;">
      <v-btn block color="primary" outlined elevation="0" small v-allowed="'edit_schedule'"
        @click="showAddClassParams()">
        {{ $t('message.add_class') }}
      </v-btn>
    </v-card-actions>
    <div v-if="!loadingSchedule && hasClasses">
      <v-list subheader three-line>
        <template v-for="(d, dayIndex) in days">
          <v-subheader class="secondary--text" :key="'day' + dayIndex" v-if="schedule[d].length > 0">
            {{ d | capitalize }}
          </v-subheader>
          <template v-for="(c, hourIndex) in schedule[d]">
            <v-list-item v-if="schedule[d].length > 0" :key="d + hourIndex" @click.stop="doNothing()"
              :style="c.color ? 'border-left: 4px solid ' + c.color + '; padding-left: 12px;' : ''">
              <v-list-item-content class="text-left">
                <v-list-item-title>
                  {{ c.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ $d(numberToDate(c.start), "time", $i18n.locale) }} - {{ $d(numberToDate(c.end),
                  "time", $i18n.locale) }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-if="c.professorUser && members.length > 0">
                  <v-icon size="16" class="mr-1" color="primary">mdi-police-badge-outline</v-icon>{{ getProfessorName(c.professorUser) }}
                  <span v-if="c.assistantProfessors && c.assistantProfessors.length" class="ml-1">(+{{
                    c.assistantProfessors.length }} {{ $t('message.more') }})</span>
                </v-list-item-subtitle>
                <v-list-item-subtitle v-if="c.location">
                  <v-icon size="14" class="mr-1">mdi-map-marker-outline</v-icon>{{ c.location }}
                </v-list-item-subtitle>
                <v-list-item-subtitle>
                  <v-tooltip v-if="c.details" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon class="mr-1" v-bind="attrs" v-on="on" color="primary"
                        size="20">mdi-text-box-check-outline</v-icon>
                    </template>
                    <span>{{ $t('message.has_details') }}</span>
                  </v-tooltip>
                  <v-tooltip v-if="c.maxCapacity > 0" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon class="mr-1" v-bind="attrs" v-on="on" color="primary" size="20">mdi-key-outline</v-icon>
                    </template>
                    <span>{{ $t('message.require_registration') }}</span>
                  </v-tooltip>
                  <v-tooltip v-if="c.free" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon class="mr-1" v-bind="attrs" v-on="on" color="primary"
                        size="20">mdi-currency-usd-off</v-icon>
                    </template>
                    <span>{{ $t('message.free') }}</span>
                  </v-tooltip>
                  <v-tooltip v-if="c.acceptVisitors" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn class="mr-4 mb-1" v-bind="attrs" v-on="on" color="primary" icon>
                        <v-icon>mdi-account-check-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('message.accepting_visitors') }}</span>
                  </v-tooltip>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="!isSmallerDisplay()">
                <v-btn small v-allowed="'edit_schedule'" icon @click="editFromSchedule(c)">
                  <v-icon>mdi-square-edit-outline</v-icon>
                </v-btn>
              </v-list-item-action>
              <v-list-item-action>
                <v-menu offset-y v-if="isSmallerDisplay()">
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" fab text small slot="activator" left>
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item v-allowed="'edit_schedule'" icon color="primary" @click="editFromSchedule(c)">
                      <v-list-item-title>
                        <v-icon>mdi-square-edit-outline</v-icon>
                        {{ $t("message.edit") }}
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item icon color="primary" @click="removeFromSchedule(c._id)">
                      <v-list-item-title>
                        <v-icon>mdi-trash-can-outline</v-icon>
                        {{ $t("message.delete") }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <v-btn v-else elevation="0" small v-allowed="'edit_schedule'" icon @click="removeFromSchedule(c._id)">
                  <v-icon>mdi-trash-can-outline</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </template>
        </template>
      </v-list>
    </div>
    <div v-else-if="loadingSchedule" class="pa-2">
      <v-skeleton-loader type="card">
      </v-skeleton-loader>
    </div>
    <div v-else class="pa-2">
      <div class="text-subtitle-1">{{ $t('message.schedule_explain1') }}</div>
      {{ $t('message.schedule_explain2') }} {{ $t('message.academies') }} {{ $t('message.schedule_explain3') }}
    </div>
    <v-dialog v-model="editClassDialog" width="90%">
      <v-card outlined>
        <v-card-actions>
          <h2>{{ $t('message.class') }}</h2>
        </v-card-actions>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="5">
              <v-text-field hide-details outlined dense :label="$t('message.class_title')"
                v-model="newClass.name"></v-text-field>
            </v-col>
            <v-col cols="12" sm="3">
              <v-select hide-details dense v-if="addingClass" :items="daysSelector" item-text="text" item-value="value"
                :label="$t('message.class_day')" v-model="newClass.day" outlined multiple></v-select>
              <v-select hide-details :disabled="true" dense v-else :items="daysSelector" item-text="text"
                item-value="value" :label="$t('message.class_day')" v-model="newClass.day" outlined></v-select>
            </v-col>
            <v-col cols="12" sm="2">
              <v-select hide-details dense :items="hours" v-model="newClass.start" item-text="display"
                item-value="value" :label="$t('message.class_start')" outlined></v-select>
            </v-col>
            <v-col cols="12" sm="2">
              <v-select hide-details dense :items="hours" v-model="newClass.end" item-text="display" item-value="value"
                :label="$t('message.class_end')" outlined></v-select>
            </v-col>
          </v-row>
          <v-row v-if="members && members.length > 0">
            <v-col cols="12">
              <v-autocomplete hide-details v-model="newClass.professorUser" :items="members"
                :label="$t('message.professor') + ' (' + $t('message.optional') + ')'" item-text="text"
                item-value="value" clearable outlined dense></v-autocomplete>
            </v-col>
          </v-row>
          <v-row v-if="members && members.length > 0">
            <v-col cols="12">
              <v-autocomplete multiple small-chips hide-details v-model="newClass.assistantProfessors" :items="members"
                :label="$t('message.assistant_professors') + ' (' + $t('message.optional') + ')'" item-text="text"
                item-value="value" clearable outlined dense></v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6">
              <v-combobox hide-details outlined dense v-model="newClass.location"
                :search-input.sync="locationInput"
                :items="availableLocations"
                :label="$t('message.location') + ' (' + $t('message.optional') + ')'"
                clearable></v-combobox>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="mb-0 pb-0">
              <v-textarea hide-details outlined dense v-model="newClass.details"
                :label="$t('message.details') + ' (' + $t('message.optional') + ')'"></v-textarea>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-list dense>
                <v-list-item>
                  <v-list-item-action>
                    <v-switch dense hide-details v-model="newClass.acceptVisitors"></v-switch>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ $t('message.accept_visitors') }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ $t('message.accept_visitors_explained') }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-action>
                    <v-switch dense hide-details v-model="newClass.free"></v-switch>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ $t('message.free') }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ $t('message.free_class_explained') }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-action>
                    <v-switch dense hide-details v-model="requireRegistration"></v-switch>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ $t('message.require_registration') }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ $t('message.require_registration_explained') }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <div v-if="requireRegistration">
                <v-text-field hide-details outlined dense v-model="newClass.maxCapacity"
                  :label="$t('message.max_capacity')"></v-text-field>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-list dense>
                <v-list-item>
                  <v-list-item-action>
                    <v-switch dense hide-details v-model="showColorPicker"></v-switch>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ $t('message.class_color') }}</v-list-item-title>
                    <v-list-item-subtitle>{{ $t('message.class_color_explained') }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <v-color-picker
                v-if="showColorPicker"
                v-model="newClass.color"
                class="ml-12"
                dot-size="25"
                hide-mode-switch
                hide-inputs
                mode="hexa"
                show-swatches
                swatches-max-height="150"
              ></v-color-picker>
            </v-col>
          </v-row>
          <div v-if="error" class="error--text">{{ $t('message.fill_all_params') }}></div>
        </v-card-text>
        <v-card-actions>
          <v-btn :loading="processing" outlined v-if="!editingClass" small color="primary" elevation="0"
            @click="addToSchedule()">
            <v-icon>mdi-plus-circle-outline</v-icon> {{ $t('message.add') }}
          </v-btn>
          <v-btn :loading="processing" v-else small outlined color="primary" elevation="0" @click="updateInSchedule()">
            <v-icon>mdi-square-edit-outline</v-icon>{{ $t('message.update') }}
          </v-btn>
          <v-btn small elevation="0" class="outlined-btn" @click="showAddClassParams(true)">
            {{ $t('message.close') }}
          </v-btn>
        </v-card-actions>
        <div class="text-caption pl-2">
          {{ $t('message.only_editing_one_class') }}
        </div>
      </v-card>
    </v-dialog>
  </v-card>
</template>
<script>
import Api from "@/services/Api";

export default {
  data() {
    return {
      newClass: { location: '' },
      schedule: {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: [],
      },
      loadingSchedule: false,
      members: [],
      hasClasses: false,
      updateMessage: null,
      selectedClassUsage: null,
      error: false,
      restrictedClasses: [],
      restrictedClassesUsage: {},
      addingClass: false,
      editingClass: false,
      processing: false,
      requireRegistration: false,
      showColorPicker: false,
      editClassDialog: null,
      locationInput: null,
      days: [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ],
      daysSelector: [
        { text: this.$t("message.monday"), value: "monday" },
        { text: this.$t("message.tuesday"), value: "tuesday" },
        { text: this.$t("message.wednesday"), value: "wednesday" },
        { text: this.$t("message.thursday"), value: "thursday" },
        { text: this.$t("message.friday"), value: "friday" },
        { text: this.$t("message.saturday"), value: "saturday" },
        { text: this.$t("message.sunday"), value: "sunday" },
      ],
      hours: this.initializeHours(),
    };
  },
  computed: {
    availableLocations() {
      const locations = new Set();
      for (const day of this.days) {
        for (const c of (this.schedule[day] || [])) {
          if (c.location) locations.add(c.location);
        }
      }
      return Array.from(locations).sort();
    },
  },
  props: {
    value: null,
  },
  created() {
    this.scheduleTemplate = [];
    Api()
      .get("admins/academy/" + this.$store.state.selectedAcademy + "/members")
      .then((response) => {
        const data = response.data;
        if (data) {
          for (const m of data.approved) {
            this.members.push({ value: m._id, text: m.fName + " " + m.lName });
          }
        }
      });
    this.loadSchedule();
  },
  filters: {
    capitalize: function (value) {
      if (!value) return "";
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },
  watch: {
    value: {
      handler: function (newValue) {
        this.schedule = newValue;
      },
    },
    locationInput(val) {
      // Mirror the live typed value into newClass.location immediately,
      // so updateInSchedule always reads the latest text regardless of
      // whether v-combobox has committed (blur/enter) yet.
      const sanitized = (val && typeof val === 'string')
        ? val.replace(/[^a-zA-Z0-9\s\-.,'/()#À-ÖØ-öø-ÿ]/g, '')
        : null;
      this.$set(this.newClass, 'location', sanitized || null);
    },
  },
  methods: {
    checkScheduleIds() {
      let fixSchedule = false;

      const days = Object.keys(this.schedule);
      if (days && days.length > 0) {
        for (const day of days) {
          let dayClasses = this.schedule[day];
          for (const c of dayClasses) {
            if (!c._id) fixSchedule = true;
            break;
          }
        }
      }

      if (fixSchedule) {
        console.log('We need to fix');
        this.updateSchedule(true);
      }
      return fixSchedule;
    },
    isRestricted(id) {
      if (this.restrictedClasses.indexOf(id) < 0)
        return false;
      return true;
    },
    loadSchedule() {
      this.loadingSchedule = true;
      Api()
        .get("admins/academy/" + this.$store.state.managedAcademy + "/schedule")
        .then((response) => {
          const data = response.data;
          if (data) {
            this.schedule = data.schedule;
            this.restrictedClasses = data.restricted;
            this.restrictedClassesUsage = data.usage;
            if (
              this.schedule &&
              (this.schedule.monday ||
                this.schedule.tuesday ||
                this.schedule.wednesday ||
                this.schedule.thursday ||
                this.schedule.friday ||
                this.schedule.saturday ||
                this.schedule.sunday)
            ) {
              //We have a schedule, pre load it
              this.hasClasses = true;
            }
            const needsFixing = this.checkScheduleIds();
            if (needsFixing) {
              const vm = this;
              setTimeout(function () {
                vm.loadSchedule();
              }, 1000);
            } else {
              this.loadingSchedule = false;
            }
          }
        });
    },
    getProfessorName(id) {
      if (this.members) {
        for (const m of this.members) {
          if (m.value == id)
            return m.text;
        }
      }
    },
    initializeHours() {
      var hours = [];
      for (let totalMinutes = 5 * 60; totalMinutes < 24 * 60; totalMinutes += 5) {
        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;
        const display = `${h}:${String(m).padStart(2, "0")}`;
        const value = parseFloat(`${h}.${String(m).padStart(2, "0")}`);
        hours.push({ display: display, value: value });
      }
      return hours;
    },
    doNothing() { },
    numberToString(time) {
      const fixed = parseFloat(time).toFixed(2);
      const [h, m] = fixed.split(".");
      return `${h}:${m || "00"}`;
    },
    showAddClassParams(clear) {
      this.editingClass = false;
      this.newClass = {};
      this.newClass.name = null;
      this.newClass.day = null;
      this.newClass.start = null;
      this.newClass.end = null;
      this.newClass.details = null;
      this.newClass.color = null;
      this.$set(this.newClass, 'location', null);
      this.locationInput = null;
      this.requireRegistration = false;
      this.showColorPicker = false;
      if (clear) {
        //Clear params
        this.editClassDialog = false;
      } else {
        this.editClassDialog = true;
        this.addingClass = true;
      }
    },
    addToSchedule() {
      this.error = false;
      if (
        !this.newClass.name ||
        !this.newClass.day ||
        !this.newClass.start ||
        !this.newClass.end
      ) {
        this.error = true;
        return;
      }
      if (Array.isArray(this.newClass.day)) {
        //Loop and add all
        for (const day of this.newClass.day) {
          var dayClasses = this.schedule[day];
          if (!dayClasses) dayClasses = [];
          dayClasses.push({
            name: this.newClass.name,
            day: day,
            start: this.newClass.start,
            end: this.newClass.end,
            professorUser: this.newClass.professorUser,
            assistantProfessors: this.newClass.assistantProfessors,
            details: this.newClass.details,
            acceptVisitors: this.newClass.acceptVisitors,
            maxCapacity: this.newClass.maxCapacity,
            free: this.newClass.free,
            color: this.showColorPicker ? (this.newClass.color?.hex || this.newClass.color) : null,
            location: this.newClass.location || null,
          });
          this.schedule[day] = this.sortedArray(dayClasses);
        }
      } else {
        //normal
        var dayClasses2 = this.schedule[this.newClass.day];
        if (!dayClasses2) dayClasses2 = [];
        dayClasses2.push({
          name: this.newClass.name,
          day: this.newClass.day,
          start: this.newClass.start,
          end: this.newClass.end,
          professorUser: this.newClass.professorUser,
          assistantProfessors: this.newClass.assistantProfessors,
          details: this.newClass.details,
          acceptVisitors: this.newClass.acceptVisitors,
          maxCapacity: this.newClass.maxCapacity,
          free: this.newClass.free,
          color: this.showColorPicker ? (this.newClass.color?.hex || this.newClass.color) : null,
          location: this.newClass.location || null,
        });
        this.schedule[this.newClass.day] = this.sortedArray(dayClasses2);
      }
      this.$emit("input", this.schedule);
      this.newClass = {};
      this.addingClass = false;
      this.hasClasses = true;
      this.updateSchedule();
    },
    updateInSchedule() {
      this.error = false;
      const classData = this.newClass;
      if (
        !this.newClass.name ||
        !this.newClass.day ||
        !this.newClass.start ||
        !this.newClass.end
      ) {
        this.error = true;
        return;
      }
      let dayClasses = this.schedule[classData.day];
      for (const c of dayClasses) {
        if (c._id == classData._id) {
          c.professorUser = classData.professorUser;
          c.assistantProfessors = classData.assistantProfessors;
          c.acceptVisitors = classData.acceptVisitors;
          c.details = classData.details;
          c.start = classData.start;
          c.end = classData.end;
          c.name = classData.name;
          c.maxCapacity = classData.maxCapacity;
          c.free = classData.free;
          c.color = this.showColorPicker ? (classData.color?.hex || classData.color) : null;
          this.$set(c, 'location', classData.location || null);
          if (!this.requireRegistration)
            c.maxCapacity = null;
          break;
        }
      }
      this.$emit("input", this.schedule);
      this.newClass = {};
      this.addingClass = false;
      this.hasClasses = true;
      this.editClassDialog = false;
      this.updateSchedule();
    },
    editFromSchedule(classData) {
      try {
        this.addingClass = false;
        //Fill the add
        this.newClass = {};
        this.newClass.name = classData.name;
        this.newClass.day = classData.day;
        this.newClass.start = classData.start;
        this.newClass.end = classData.end;
        this.newClass._id = classData._id;
        this.newClass.professorUser = classData.professorUser;
        this.newClass.assistantProfessors = classData.assistantProfessors;
        this.newClass.details = classData.details;
        this.newClass.acceptVisitors = classData.acceptVisitors;
        this.newClass.maxCapacity = classData.maxCapacity;
        this.newClass.free = classData.free;
        this.newClass.color = classData.color;
        this.$set(this.newClass, 'location', classData.location || null);
        this.locationInput = classData.location || null;
        if (classData.maxCapacity)
          this.requireRegistration = true;
        this.showColorPicker = !!classData.color;
        this.editingClass = true;
        this.editClassDialog = true;
      } catch (e) {
        if (e)
          console.log(e);
      }
    },
    removeFromSchedule(id) {
      const days = Object.keys(this.schedule);
      let found = false;
      if (days && days.length > 0) {
        for (const day of days) {
          let dayClasses = this.schedule[day];
          let index = 0;
          for (const c of dayClasses) {
            if (c._id == id) {
              dayClasses.splice(index, 1);
              found = true;
              break;
            }
            index++;
          }
        }
      }
      if (found) {
        Api()
          .delete("admins/academy/" + this.$store.state.managedAcademy + "/schedule/" + id)
          .then(() => {
            this.showAlert(
              "success",
              this.$t("message.updated")
            );
          })
          .catch((e) => {
            if (e)
              this.showAlert(
                "error",
                "Oops!",
                this.$t("message.error_generic")
              );
          });
      }
    },
    compare(a, b) {
      if (a.start < b.start) return -1;
      if (a.start > b.start) return 1;
      return 0; //Improve the base case if they start at the same time
    },
    sortedArray(array) {
      if (array) {
        return array.sort(this.compare);
      }
      return array;
    },
    numberToDate(time) {
      const fixed = parseFloat(time).toFixed(2);
      const [h, m] = fixed.split(".");
      const d = new Date();
      d.setHours(parseInt(h, 10));
      d.setMinutes(parseInt(m || "0", 10));
      d.setSeconds(0);
      return d;
    },
    updateSchedule(skipAlert) {
      //befor eupdating, we need to find the editing one and add it
      try {
        this.processing = true;
        const data = [
          {
            propName: "schedule",
            value: this.schedule,
          },
        ];
        Api()
          .patch("admins/academy/" + this.$store.state.selectedAcademy, {
            values: data,
          })
          .then((response) => {
            const data = response.data;
            if (data) {
              this.editingClass = false;
              this.editingClass = false;
              this.editClassDialog = false;
              this.newClass.professorUser = null;
              this.newClass.assistantProfessors = null;
              this.newClass.details = null;
              this.newClass.acceptVisitors = null;
              this.newClass.maxCapacity = null;
              this.newClass.free = false;
              this.requireRegistration = false;
              this.processing = false;
              if (!skipAlert)
                this.showAlert(
                  "success",
                  this.$t("message.updated")
                );
            }
          })
          .catch((e) => {
            if (e)
              this.showAlert(
                "error",
                "Oops!",
                this.$t("message.error_generic")
              );
          });
      } catch (e) {
        console.log(e);
      }
    },
  },
};
</script>

<style scoped>
::v-deep .v-list-item__title,
::v-deep .v-list-item__subtitle {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
}
</style>